import { useEffect, useState } from 'react';
import Button from '../../components/Button';
import { formatCurrency } from '../../data/mockData';
import { listLots, repriceLots, repriceErrorMessage } from '../../api/inventory';
import { useAuth } from '../../context/AuthContext';
import { availableLots, sameShelfMoney } from '../../utils/lots';
import { formatStock } from '../../utils/soldBy';
import form from '../../styles/form.module.css';
import { ProductCombobox } from './StockMoveForm';
import styles from './Inventory.module.css';

function draftFromLot(lot) {
  return {
    sellingPrice: lot.sellingPrice ?? '',
    mrp: lot.mrp ?? '',
    mrpTouched: false,
    costPrice: lot.costPrice ?? '',
    costTouched: false,
  };
}

export default function LotRepricePanel({ products, initialProductId = '' }) {
  const { user } = useAuth();
  const [productId, setProductId] = useState(initialProductId);
  const [lots, setLots] = useState([]);
  const [lotsLoading, setLotsLoading] = useState(false);
  const [lotsError, setLotsError] = useState('');
  const [lotsSupported, setLotsSupported] = useState(true);
  const [drafts, setDrafts] = useState({});
  const [savingLotId, setSavingLotId] = useState('');
  const [rowError, setRowError] = useState(null);
  const [notice, setNotice] = useState('');

  const product = products.find((p) => p.id === productId) || null;

  useEffect(() => {
    if (initialProductId) setProductId(initialProductId);
  }, [initialProductId]);

  useEffect(() => {
    if (!productId || !user?.idToken) {
      setLots([]);
      setDrafts({});
      setLotsError('');
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setLotsLoading(true);
      setLotsError('');
      setNotice('');
      setRowError(null);
      try {
        const data = await listLots(productId, user.idToken);
        if (cancelled) return;
        const open = availableLots(data.lots);
        setLotsSupported(true);
        setLots(open);
        setDrafts(Object.fromEntries(open.map((lot) => [lot.lotId, draftFromLot(lot)])));
      } catch (err) {
        if (cancelled) return;
        if (err.status === 404 || err.message === 'not_found') {
          setLotsSupported(false);
          setLots([]);
          setDrafts({});
        } else {
          setLotsError(err.message || 'Could not load lots.');
        }
      } finally {
        if (!cancelled) setLotsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [productId, user?.idToken]);

  const setDraft = (lotId, patch) => {
    setDrafts((prev) => ({
      ...prev,
      [lotId]: { ...prev[lotId], ...patch },
    }));
    setRowError((prev) => (prev?.lotId === lotId ? null : prev));
    setNotice('');
  };

  const saveLot = async (lot) => {
    if (!product || savingLotId) return;
    const draft = drafts[lot.lotId] || draftFromLot(lot);
    const sell = Number(draft.sellingPrice);
    if (!Number.isFinite(sell) || sell < 0) {
      setRowError({ lotId: lot.lotId, message: 'Enter a sell price of ₹0 or more.' });
      return;
    }
    const mrpEdited = draft.mrpTouched && draft.mrp !== '' && draft.mrp != null
      && !sameShelfMoney(draft.mrp, lot.mrp);
    const costEdited = draft.costTouched && !sameShelfMoney(draft.costPrice, lot.costPrice);
    const priceEdited = !sameShelfMoney(sell, lot.sellingPrice);
    if (draft.mrpTouched && draft.mrp !== '' && draft.mrp != null && !(Number(draft.mrp) >= 0)) {
      setRowError({ lotId: lot.lotId, message: 'Enter an MRP of ₹0 or more, or leave it as it is.' });
      return;
    }
    if (draft.costTouched && (draft.costPrice === '' || !(Number(draft.costPrice) >= 0))) {
      setRowError({ lotId: lot.lotId, message: 'Enter a cost of ₹0 or more, or leave it as it is.' });
      return;
    }
    if (!priceEdited && !mrpEdited && !costEdited) {
      setRowError({ lotId: lot.lotId, message: 'Change the sell price, MRP, or cost before updating this lot.' });
      return;
    }
    setSavingLotId(lot.lotId);
    setRowError(null);
    setNotice('');
    try {
      const result = await repriceLots(product.id, {
        sellingPrice: sell,
        mrp: mrpEdited ? Number(draft.mrp) : undefined,
        costPrice: costEdited ? Number(draft.costPrice) : undefined,
        lotIds: [lot.lotId],
      }, user.idToken);
      const open = availableLots(result.lots);
      setLots(open);
      setDrafts(Object.fromEntries(open.map((row) => [row.lotId, draftFromLot(row)])));
      setNotice(`Shelf lot updated. Catalog price stays ${formatCurrency(Number(product.price) || 0)} and catalog cost stays ${formatCurrency(Number(product.costPrice) || 0)}.`);
    } catch (err) {
      const code = err?.code || err?.message;
      if (code === 'lot_mismatch' || code === 'lot_not_open') {
        try {
          const data = await listLots(product.id, user.idToken);
          const open = availableLots(data.lots);
          setLots(open);
          setDrafts(Object.fromEntries(open.map((row) => [row.lotId, draftFromLot(row)])));
        } catch {
          // Show the reprice error either way.
        }
      }
      setRowError({ lotId: lot.lotId, message: repriceErrorMessage(err) });
    } finally {
      setSavingLotId('');
    }
  };

  return (
    <div className={styles.formCard}>
      <p className={styles.formIntro}>
        Change this shelf lot’s sell price, MRP, or cost. Quantity stays the same. The product’s catalog price and catalog cost stay as they are.
      </p>
      <label className={form.field}>
        <span className={form.label}>Product</span>
        <ProductCombobox products={products} productId={productId} onChange={setProductId} />
        {product && (
          <span className={form.hint}>
            Default / online catalog price {formatCurrency(Number(product.price) || 0)}
            {product.costPrice != null && product.costPrice !== '' ? ` · Cost ${formatCurrency(Number(product.costPrice) || 0)}` : ''}
            {product.mrp != null && product.mrp !== '' ? ` · MRP ${formatCurrency(Number(product.mrp) || 0)}` : ''}
            . These stay put until you save the product.
          </span>
        )}
      </label>

      {lotsLoading && <p className={form.hint}>Loading open lots…</p>}
      {lotsError && <p className={styles.formError}>{lotsError}</p>}
      {!lotsLoading && product && !lotsSupported && (
        <p className={form.hint}>Lot prices are not available for this product yet.</p>
      )}
      {!lotsLoading && product && lotsSupported && lots.length === 0 && (
        <p className={form.hint}>No open lots. Receive stock with Stock In before setting a shelf price.</p>
      )}
      {notice && <p className={styles.repriceNotice}>{notice}</p>}

      {lots.length > 0 && (
        <div className={styles.repriceList}>
          {lots.map((lot) => {
            const draft = drafts[lot.lotId] || draftFromLot(lot);
            const busy = savingLotId === lot.lotId;
            return (
              <div key={lot.lotId} className={styles.repriceRow}>
                <div className={styles.repriceMeta}>
                  <strong>{formatStock(lot.qty, product)} on shelf</strong>
                  <span>
                    Now {formatCurrency(Number(lot.sellingPrice) || 0)}
                    {lot.mrp != null && lot.mrp !== '' ? ` · MRP ${formatCurrency(Number(lot.mrp) || 0)}` : ''}
                    {lot.costPrice != null && lot.costPrice !== '' ? ` · Cost ${formatCurrency(Number(lot.costPrice) || 0)}` : ''}
                  </span>
                </div>
                <label className={form.field}>
                  <span className={form.label}>
                    {product && String(product.soldBy || '').toUpperCase() === 'WEIGHT'
                      ? `Sell rate / ${product.unit || 'kg'}`
                      : 'Sell price'}
                  </span>
                  <div className={form.prefixInput}>
                    <span>₹</span>
                    <input
                      className={form.input}
                      type="number"
                      min="0"
                      step="0.01"
                      value={draft.sellingPrice}
                      onChange={(e) => setDraft(lot.lotId, { sellingPrice: e.target.value })}
                    />
                  </div>
                </label>
                <label className={form.field}>
                  <span className={form.label}>MRP</span>
                  <div className={form.prefixInput}>
                    <span>₹</span>
                    <input
                      className={form.input}
                      type="number"
                      min="0"
                      step="0.01"
                      value={draft.mrp}
                      onChange={(e) => setDraft(lot.lotId, { mrp: e.target.value, mrpTouched: true })}
                    />
                  </div>
                  <span className={form.hint}>Leave MRP as it is to keep this lot’s MRP.</span>
                </label>
                <label className={form.field}>
                  <span className={form.label}>Cost</span>
                  <div className={form.prefixInput}>
                    <span>₹</span>
                    <input
                      className={form.input}
                      type="number"
                      min="0"
                      step="0.01"
                      value={draft.costPrice}
                      onChange={(e) => setDraft(lot.lotId, { costPrice: e.target.value, costTouched: true })}
                    />
                  </div>
                  <span className={form.hint}>Leave cost as it is to keep this lot’s cost. Catalog cost is not changed.</span>
                </label>
                {rowError?.lotId === lot.lotId && <p className={styles.formError}>{rowError.message}</p>}
                <Button
                  type="button"
                  size="sm"
                  onClick={() => saveLot(lot)}
                  disabled={Boolean(savingLotId)}
                >
                  {busy ? 'Updating…' : 'Update this lot'}
                </Button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
